import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, retry, switchMap, throwError } from 'rxjs';
import environment from '@environments/environment';
import { AuthService } from './auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const allowedOrigins = [
      environment.appBaseHref,
      environment.irisApi,
      environment.jemmaApi,
    ];

    // Skip modifying requests not going to allowed origins
    if (!allowedOrigins.some((url) => request.urlWithParams.includes(url))) {
      return next.handle(request);
    }

    return this.handleAuthHeader(request, next).pipe(
      catchError((error: string | HttpErrorResponse) => {
        console.error('Token error', error);

        const isUnauthorized =
          typeof error === 'string'
            ? error === 'You do not have the credentials.'
            : error?.status === 401;

        const isAccessTokenMissing =
          typeof error !== 'string' &&
          error?.message === 'Access token not available';

        // Retry the request if unauthorized and token is available
        if (isUnauthorized && !isAccessTokenMissing) {
          if (request.headers.has('x-refreshed-token')) {
            // Prevent infinite retry loop if already retried
            return throwError(() => error);
          }
          return this.handleTokenRefresh(request, next);
        }

        // Logout for missing access token or other auth errors
        if (isAccessTokenMissing || (isUnauthorized && isAccessTokenMissing)) {
          this._authService.addLogoutInsights('TokenExpired', { error });
          console.warn('Access token missing or expired. Logging out.');
          this._router.navigate(['/auth/logout']);
        }

        return throwError(() => error);
      }),
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this._authService.getAccessToken().pipe(
      switchMap((accessToken: string | undefined) => {
        if (accessToken) {
          const updatedRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${accessToken}`,
              'x-refreshed-token': 'true',
              ...(request.body instanceof FormData
                ? {}
                : { 'Content-Type': 'application/json' }),
            },
          });
          return next.handle(updatedRequest);
        }

        // Token refresh failed, logout
        this._authService.addLogoutInsights('TokenRefreshFailed', {
          error: 'Token refresh failed',
        });
        console.warn('Token refresh failed. Logging out.');
        this._router.navigate(['/auth/logout']);
        return throwError(() => new Error('Token refresh failed'));
      }),
    );
  }

  private handleAuthHeader(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this._authService.getAccessToken().pipe(
      retry({ count: 1, delay: 100 }),
      switchMap((accessToken: string | undefined) => {
        if (accessToken) {
          const updatedRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${accessToken}`,
              ...(request.body instanceof FormData
                ? {}
                : { 'Content-Type': 'application/json' }),
            },
          });
          return next.handle(updatedRequest);
        }

        // Proceed without token if unavailable
        return next.handle(request);
      }),
    );
  }
}
